import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { consultaCadastralService, usersService } from '../services/api';
import '../styles/ConsultaCadastral.css';

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const formatCPFInput = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const formatCpfDisplay = (cpf) => {
  const n = String(cpf || '').replace(/\D/g, '');
  if (n.length !== 11) return cpf;
  return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR');
};

const soDigitos = (v) => String(v || '').replace(/\D/g, '');

const formatCEPInput = (value) =>
  soDigitos(value).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');

const formatTelefoneInput = (value) => {
  const n = soDigitos(value).slice(0, 11);
  if (n.length <= 2) return n;
  if (n.length <= 6) return n.replace(/(\d{2})(\d+)/, '($1) $2');
  if (n.length <= 10) return n.replace(/(\d{2})(\d{4})(\d+)/, '($1) $2-$3');
  return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

const formatDataInput = (value) =>
  soDigitos(value)
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2');

const TIPOS_CPF = ['cpf', 'cpf2', 'cpf4', 'score'];

const TIPOS = {
  bin: {
    label: 'BIN',
    campo: 'BIN do cartão',
    placeholder: '000000',
    mask: (v) => soDigitos(v).slice(0, 8),
    maxLength: 8,
    dica: 'Informe os 6 a 8 primeiros dígitos do cartão.'
  },
  cep: { label: 'CEP', campo: 'CEP', placeholder: '00000-000', mask: formatCEPInput, maxLength: 9 },
  cpf: {
    label: 'CPF completo',
    campo: 'CPF',
    placeholder: '000.000.000-00',
    mask: formatCPFInput,
    maxLength: 14,
    dica: 'Consulta completa na base principal, com DataNexus como alternativa.'
  },
  cpf2: { label: 'CPF 2', campo: 'CPF', placeholder: '000.000.000-00', mask: formatCPFInput, maxLength: 14 },
  cpf4: { label: 'CPF 4', campo: 'CPF', placeholder: '000.000.000-00', mask: formatCPFInput, maxLength: 14 },
  rg: {
    label: 'RG',
    campo: 'Número do RG',
    placeholder: 'Somente números e letras',
    mask: (v) => v.replace(/[^0-9A-Za-z]/g, '').slice(0, 20),
    maxLength: 20
  },
  score: { label: 'Score', campo: 'CPF', placeholder: '000.000.000-00', mask: formatCPFInput, maxLength: 14 },
  email: { label: 'E-mail', campo: 'E-mail', placeholder: 'nome@dominio.com', type: 'email', maxLength: 150 },
  nome: { label: 'Nome', campo: 'Nome completo', placeholder: 'Ex.: Maria da Silva', maxLength: 150 },
  nascimento: {
    label: 'Nascimento',
    campo: 'Data de nascimento',
    placeholder: 'dd/mm/aaaa',
    mask: formatDataInput,
    maxLength: 10
  },
  mae: { label: 'Mãe', campo: 'Nome da mãe', placeholder: 'Nome completo da mãe', maxLength: 150 },
  pai: { label: 'Pai', campo: 'Nome do pai', placeholder: 'Nome completo do pai', maxLength: 150 },
  telefone: {
    label: 'Telefone',
    campo: 'Telefone',
    placeholder: '(00) 00000-0000',
    mask: formatTelefoneInput,
    maxLength: 15
  }
};

const GRUPOS_TIPOS = [
  { titulo: 'Financeiros', tipos: ['bin', 'cep', 'cpf', 'cpf2', 'cpf4', 'rg', 'score', 'email'] },
  { titulo: 'Pessoas', tipos: ['nome', 'nascimento', 'mae', 'pai', 'telefone'] }
];

const formatValorConsulta = (tipo, valor) => {
  if (!valor) return '—';
  if (TIPOS_CPF.includes(tipo)) return formatCpfDisplay(valor);
  if (tipo === 'cep') return formatCEPInput(valor);
  if (tipo === 'telefone') return formatTelefoneInput(valor);
  return valor;
};

const PALAVRAS_ROTULO = {
  mae: 'mãe', nascimento: 'nascimento', dt: 'data', data: 'data', endereco: 'endereço',
  numero: 'número', telefone: 'telefone', telefones: 'telefones', obito: 'óbito',
  codigo: 'código', situacao: 'situação', orgao: 'órgão', emissao: 'emissão',
  municipio: 'município', pais: 'país', credito: 'crédito', debito: 'débito',
  nivel: 'nível', bandeira: 'bandeira', descricao: 'descrição', razao: 'razão',
  informacoes: 'informações', veiculos: 'veículos', qt: 'qtd', pct: '%', nome: 'nome',
  sexo: 'sexo', renda: 'renda', presumida: 'presumida', faixa: 'faixa', vinculo: 'vínculo',
  vinculos: 'vínculos', parentesco: 'parentesco', responsavel: 'responsável'
};

const SIGLAS_ROTULO = ['cpf', 'cnpj', 'rg', 'cep', 'uf', 'bin', 'cbo', 'cnae', 'cns', 'id', 'pis', 'ddd'];

const formatRotulo = (chave) => {
  const partes = String(chave)
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((p) => {
      const l = p.toLowerCase();
      if (SIGLAS_ROTULO.includes(l)) return l.toUpperCase();
      return PALAVRAS_ROTULO[l] || l;
    });
  if (!partes.length) return String(chave);
  const texto = partes.join(' ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const isVazio = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') {
    const s = v.trim().toUpperCase();
    return !s || s === 'SEM RESULTADO' || s === 'NULL' || s === 'UNDEFINED';
  }
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.values(v).every(isVazio);
  return false;
};

const formatValorCampo = (chave, valor) => {
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  const s = String(valor).replace(/\\\//g, '/').trim();
  const k = String(chave).toLowerCase();
  const d = soDigitos(s);
  if (k.includes('cpf') && d.length === 11 && /^[\d.\-\s]+$/.test(s)) return formatCpfDisplay(d);
  if (k.includes('cep') && d.length === 8 && /^[\d.\-\s]+$/.test(s)) return formatCEPInput(d);
  if ((k.includes('telefone') || k.includes('celular') || k === 'fone') && (d.length === 10 || d.length === 11) && /^[\d()\-\s+]+$/.test(s)) {
    return formatTelefoneInput(d);
  }
  return s;
};

const isPrimitivo = (v) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

const extrairCpfLinha = (row) => {
  const chave = Object.keys(row).find((k) => /(^|_)cpf($|_)/i.test(k) || /^documento$/i.test(k));
  if (!chave) return null;
  const d = soDigitos(row[chave]);
  return d.length === 11 ? d : null;
};

function Campo({ label, valor }) {
  return (
    <div className="cc-field">
      <label>{label}</label>
      <span>{fmt(valor)}</span>
    </div>
  );
}

function TabelaSimples({ colunas, linhas, vazio = 'Nenhum registro.' }) {
  if (!linhas || linhas.length === 0) {
    return <p style={{ color: 'var(--texto-secundario)' }}>{vazio}</p>;
  }
  return (
    <div className="cc-table-wrap">
      <table className="cc-table">
        <thead>
          <tr>
            {colunas.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((row, i) => (
            <tr key={i}>
              {colunas.map((c) => (
                <td key={c.key}>{c.render ? c.render(row) : fmt(row[c.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultadoCadastral({ data }) {
  const [aba, setAba] = useState('resumo');
  const resumo = data?.resumo || {};
  const pessoa = data?.pessoa || {};
  const documentos = data?.documentos || {};
  const metadados = data?.metadados || {};
  const contatos = data?.contatos || {};
  const dadosProf = data?.dadosProfissionais || {};
  const atualidade = data?.atualidade || {};
  const planos = data?.planos?.registros || [];
  const enderecos = data?.enderecos || [];
  const claro = data?.claro?.registros || [];
  const oi = data?.oi?.registros || [];
  const vivo = data?.vivoPos?.registros || [];
  const energia = data?.energia?.registros || [];
  const socios = data?.empresas?.sociosRelacionados || [];
  const empresas = data?.empresas?.empresasRelacionadas || [];

  const telecomAbas = [
    { id: 'claro', label: `Claro (${data?.claro?.totalRegistros || 0})`, registros: claro, show: (data?.claro?.totalRegistros || 0) > 0 },
    { id: 'oi', label: `Oi (${data?.oi?.totalRegistros || 0})`, registros: oi, show: (data?.oi?.totalRegistros || 0) > 0 },
    { id: 'vivo', label: `Vivo (${data?.vivoPos?.totalRegistros || 0})`, registros: vivo, show: (data?.vivoPos?.totalRegistros || 0) > 0 },
    { id: 'energia', label: `Energia (${data?.energia?.totalRegistros || 0})`, registros: energia, show: (data?.energia?.totalRegistros || 0) > 0 }
  ].filter((t) => t.show);

  const [telecomAba, setTelecomAba] = useState(telecomAbas[0]?.id || 'claro');

  const abas = [
    { id: 'resumo', label: 'Resumo' },
    { id: 'identificacao', label: 'Identificação' },
    { id: 'contatos', label: 'Contatos' },
    { id: 'enderecos', label: 'Endereços' },
    { id: 'atualidade', label: 'Mais recentes' },
    { id: 'planos', label: 'Planos' },
    ...(telecomAbas.length ? [{ id: 'telecom', label: 'Telecom' }] : []),
    ...((socios.length || empresas.length) ? [{ id: 'empresas', label: 'Empresas' }] : []),
    { id: 'profissional', label: 'Profissional' }
  ];

  const colTelefone = [
    { key: 'numeroFormatado', label: 'Telefone' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'operadora', label: 'Operadora' },
    { key: 'whatsapp', label: 'WhatsApp', render: (r) => (r.whatsapp ? 'Sim' : 'Não') },
    { key: 'fonteMaisRecente', label: 'Fonte' },
    { key: 'ultimaAtualizacao', label: 'Atualização' }
  ];

  const colEmail = [
    { key: 'EMAIL', label: 'E-mail' },
    { key: 'DOMINIO', label: 'Domínio' },
    { key: 'STATUS_VT', label: 'Status' },
    { key: 'FONTE', label: 'Fonte' },
    { key: 'ULTIMA_ATUALIZACAO', label: 'Atualização' }
  ];

  const colEndereco = [
    { key: 'enderecoCompleto', label: 'Endereço' },
    { key: 'cepFormatado', label: 'CEP' },
    { key: 'fonteMaisRecente', label: 'Fonte' },
    { key: 'ultimaAtualizacao', label: 'Atualização' }
  ];

  const colPlano = [
    { key: 'fonte', label: 'Fonte' },
    { key: 'plano', label: 'Plano' },
    { key: 'servico', label: 'Serviço' },
    { key: 'plataforma', label: 'Plataforma' },
    { key: 'statusRegistro', label: 'Status' },
    { key: 'formaPagamento', label: 'Pagamento' },
    { key: 'dataInstalacao', label: 'Instalação' },
    { key: 'ultimoConhecimento', label: 'Último conhecimento' }
  ];

  const colTelecom = [
    { key: 'fonte', label: 'Fonte' },
    { key: 'nome', label: 'Nome' },
    { key: 'email', label: 'E-mail' },
    { key: 'score', label: 'Score' },
    { key: 'statusRegistro', label: 'Status' },
    { key: 'plano', label: 'Plano' },
    {
      key: 'endereco',
      label: 'Endereço',
      render: (r) => fmt(r.endereco?.enderecoCompleto)
    },
    { key: 'ultimaAtualizacao', label: 'Atualização' }
  ];

  const telecomAtual = telecomAbas.find((t) => t.id === telecomAba)?.registros || [];

  return (
    <div className="cc-card">
      <div className="cc-card-header">
        <span className="cc-card-header-title">
          {fmt(resumo.nome)} — {fmt(resumo.cpfFormatado)}
        </span>
        <span className={`cc-badge ${data.encontrado ? 'cc-badge-success' : 'cc-badge-muted'}`}>
          {data.encontrado ? 'Encontrado' : 'Não encontrado'}
        </span>
        {data.fonteConsulta === 'datanexus' && (
          <span className="cc-badge cc-badge-muted" title="Dados obtidos via API fallback">
            DataNexus
          </span>
        )}
      </div>
      <div className="cc-card-body">
        <div className="cc-tabs">
          {abas.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`cc-tab ${aba === t.id ? 'active' : ''}`}
              onClick={() => setAba(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {aba === 'resumo' && (
          <>
            <div className="cc-resumo-grid">
              <div className="cc-resumo-item">
                <label>Nome</label>
                <span>{fmt(resumo.nome)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>CPF</label>
                <span>{fmt(resumo.cpfFormatado)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>Nascimento</label>
                <span>{fmt(resumo.nascimento)} ({fmt(resumo.idade)} anos)</span>
              </div>
              <div className="cc-resumo-item">
                <label>Melhor e-mail</label>
                <span>{fmt(resumo.melhorEmail)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>Melhor telefone</label>
                <span>{fmt(resumo.melhorTelefone)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>Telefones</label>
                <span>{fmt(resumo.totalTelefones)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>E-mails</label>
                <span>{fmt(resumo.totalEmails)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>Endereços</label>
                <span>{fmt(resumo.totalEnderecos)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>Planos</label>
                <span>{fmt(resumo.totalPlanos)}</span>
              </div>
              <div className="cc-resumo-item">
                <label>Empresas</label>
                <span>{fmt(resumo.totalEmpresasRelacionadas)}</span>
              </div>
            </div>
            {(resumo.fontesAtivas || []).length > 0 && (
              <div className="cc-chips">
                {(resumo.fontesAtivas || []).map((f) => (
                  <span key={f} className="cc-chip">{f}</span>
                ))}
              </div>
            )}
          </>
        )}

        {aba === 'identificacao' && (
          <div className="cc-field-grid">
            <Campo label="Nome" valor={pessoa.nome} />
            <Campo label="CPF" valor={pessoa.cpfFormatado} />
            <Campo label="Nascimento" valor={pessoa.nascimento} />
            <Campo label="Idade" valor={pessoa.idade} />
            <Campo label="E-mail principal" valor={pessoa.emailPrincipal} />
            <Campo label="Último plano" valor={pessoa.ultimoConhecimentoPlano} />
            <Campo label="Atualização cadastral" valor={pessoa.ultimaAtualizacaoCadastral} />
            <Campo label="Mãe" valor={pessoa.filiacao?.mae} />
            <Campo label="Pai" valor={pessoa.filiacao?.pai} />
            <Campo label="RG" valor={documentos.rgNumero} />
            <Campo label="Órgão emissor" valor={documentos.rgOrgaoEmissor} />
            <Campo label="UF RG" valor={documentos.rgUf} />
            <Campo label="CNS" valor={documentos.cns} />
            <Campo label="Título eleitor" valor={documentos.tituloEleitor} />
            <Campo label="Sexo" valor={metadados.sexo} />
            <Campo label="Estado civil" valor={metadados.estadoCivil} />
            <Campo label="Município nascimento" valor={metadados.municipioNascimento} />
            <Campo label="Classe social" valor={metadados.classeSocial} />
            <Campo label="Status CPF" valor={metadados.statusCpf} />
            <Campo label="Óbito" valor={metadados.obito} />
            <Campo label="Inclusão Serasa" valor={metadados.dtInclusaoSerasa} />
          </div>
        )}

        {aba === 'contatos' && (
          <>
            <h4 style={{ marginBottom: '0.75rem', color: 'var(--texto-primario)' }}>Telefones</h4>
            <TabelaSimples colunas={colTelefone} linhas={contatos.telefones} />
            <h4 style={{ margin: '1.5rem 0 0.75rem', color: 'var(--texto-primario)' }}>E-mails</h4>
            <TabelaSimples colunas={colEmail} linhas={contatos.emails} />
          </>
        )}

        {aba === 'enderecos' && (
          <TabelaSimples colunas={colEndereco} linhas={enderecos} />
        )}

        {aba === 'atualidade' && (
          <div className="cc-field-grid">
            <Campo label="Telefone mais atual" valor={atualidade.telefoneMaisAtual?.numeroFormatado} />
            <Campo label="E-mail mais atual" valor={atualidade.emailMaisAtual?.EMAIL} />
            <Campo label="Endereço mais atual" valor={atualidade.enderecoMaisAtual?.enderecoCompleto} />
            <Campo label="Serviço mais atual" valor={atualidade.servicoMaisAtual?.servico} />
            <Campo label="Fonte serviço" valor={atualidade.servicoMaisAtual?.fonte} />
          </div>
        )}

        {aba === 'planos' && (
          <TabelaSimples colunas={colPlano} linhas={planos} />
        )}

        {aba === 'telecom' && telecomAbas.length > 0 && (
          <>
            <div className="cc-tabs" style={{ marginBottom: '1rem' }}>
              {telecomAbas.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`cc-tab ${telecomAba === t.id ? 'active' : ''}`}
                  onClick={() => setTelecomAba(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <TabelaSimples colunas={colTelecom} linhas={telecomAtual} />
          </>
        )}

        {aba === 'empresas' && (
          <>
            {socios.length > 0 && (
              <>
                <h4 style={{ marginBottom: '0.75rem' }}>Sócios relacionados</h4>
                <TabelaSimples
                  colunas={[
                    { key: 'nome', label: 'Nome' },
                    { key: 'documento', label: 'Documento' },
                    { key: 'qualificacaoId', label: 'Qualificação' },
                    { key: 'dataEntrada', label: 'Entrada' },
                    { key: 'cnpjBase', label: 'CNPJ base' }
                  ]}
                  linhas={socios}
                />
              </>
            )}
            {empresas.length > 0 && (
              <>
                <h4 style={{ margin: '1.5rem 0 0.75rem' }}>Empresas relacionadas</h4>
                <TabelaSimples
                  colunas={[
                    { key: 'cnpjFormatado', label: 'CNPJ' },
                    { key: 'razaoSocial', label: 'Razão social' },
                    { key: 'nomeFantasia', label: 'Fantasia' },
                    { key: 'situacao', label: 'Situação' },
                    { key: 'dataAbertura', label: 'Abertura' },
                    { key: 'email', label: 'E-mail' }
                  ]}
                  linhas={empresas}
                />
              </>
            )}
          </>
        )}

        {aba === 'profissional' && (
          <div className="cc-field-grid">
            <Campo label="CBO" valor={dadosProf.cbo} />
            <Campo label="Renda" valor={dadosProf.rendaFormatada || dadosProf.renda} />
            <Campo label="Renda média familiar" valor={dadosProf.rendaMediaFamiliar} />
            <Campo label="CNAE" valor={dadosProf.cnae} />
            <Campo label="Descrição CNAE" valor={dadosProf.cnaeDescricao} />
            <Campo label="Score Claro" valor={dadosProf.scoreClaro} />
            <Campo label="Status Receita" valor={dadosProf.stReceita} />
            <Campo label="Base Receita" valor={dadosProf.bsReceita} />
            <Campo label="Plano Claro" valor={dadosProf.planoClaro} />
            <Campo label="Pagamento Claro" valor={dadosProf.formaPagamentoClaro} />
            <Campo label="Status registro Claro" valor={dadosProf.statusRegistroClaro} />
          </div>
        )}
      </div>
    </div>
  );
}

function TabelaRegistros({ registros, onConsultarCpf }) {
  const objetos = registros.filter((r) => r && typeof r === 'object' && !Array.isArray(r));
  const chaves = [];
  objetos.forEach((r) => {
    Object.keys(r).forEach((k) => {
      if (!chaves.includes(k) && isPrimitivo(r[k])) chaves.push(k);
    });
  });
  const colunasVisiveis = chaves.filter((k) => objetos.some((r) => !isVazio(r[k])));
  const temCpf = Boolean(onConsultarCpf) && objetos.some((r) => extrairCpfLinha(r));

  const colunas = colunasVisiveis.map((k) => ({
    key: k,
    label: formatRotulo(k),
    render: (r) => (isVazio(r[k]) ? '—' : formatValorCampo(k, r[k]))
  }));

  if (temCpf) {
    colunas.push({
      key: '__acao',
      label: '',
      render: (r) => {
        const cpf = extrairCpfLinha(r);
        return cpf ? (
          <button type="button" className="cc-btn cc-btn-secondary cc-btn-sm" onClick={() => onConsultarCpf(cpf)}>
            Consultar CPF
          </button>
        ) : null;
      }
    });
  }

  return <TabelaSimples colunas={colunas} linhas={objetos} />;
}

function BlocoDados({ titulo, valor, onConsultarCpf }) {
  if (isVazio(valor)) return null;

  if (Array.isArray(valor)) {
    const temObjetos = valor.some((v) => v && typeof v === 'object');
    return (
      <div className="cc-section">
        {titulo && (
          <div className="cc-section-title">
            {titulo}
            <span className="cc-section-count">
              {valor.length} {valor.length === 1 ? 'registro' : 'registros'}
            </span>
          </div>
        )}
        {temObjetos ? (
          <TabelaRegistros registros={valor} onConsultarCpf={onConsultarCpf} />
        ) : (
          <div className="cc-chips" style={{ marginTop: 0 }}>
            {valor.filter((v) => !isVazio(v)).map((v, i) => (
              <span key={i} className="cc-chip">{formatValorCampo(titulo || '', v)}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (typeof valor !== 'object') {
    return (
      <div className="cc-section">
        {titulo && <div className="cc-section-title">{titulo}</div>}
        <div className="cc-field-card"><span>{formatValorCampo(titulo || '', valor)}</span></div>
      </div>
    );
  }

  const entradas = Object.entries(valor).filter(([, v]) => !isVazio(v));
  const simples = entradas.filter(([, v]) => isPrimitivo(v));
  const compostos = entradas.filter(([, v]) => !isPrimitivo(v));

  return (
    <div className="cc-section">
      {titulo && <div className="cc-section-title">{titulo}</div>}
      {simples.length > 0 && (
        <div className="cc-field-grid" style={{ marginBottom: compostos.length ? '1.5rem' : 0 }}>
          {simples.map(([k, v]) => (
            <div key={k} className="cc-field-card">
              <label>{formatRotulo(k)}</label>
              <span>{formatValorCampo(k, v)}</span>
            </div>
          ))}
        </div>
      )}
      {compostos.map(([k, v]) => (
        <BlocoDados key={k} titulo={formatRotulo(k)} valor={v} onConsultarCpf={onConsultarCpf} />
      ))}
    </div>
  );
}

function ResultadoDatanexus({ data, onConsultarCpf }) {
  const tipo = data?.tipo;
  const dados = data?.dados;
  const rotuloTipo = TIPOS[tipo]?.label || String(tipo || '').toUpperCase();

  return (
    <div className="cc-card">
      <div className="cc-card-header" style={{ flexWrap: 'wrap' }}>
        <span className="cc-badge cc-badge-tipo">{rotuloTipo}</span>
        <span className="cc-card-header-title">{formatValorConsulta(tipo, data?.valor)}</span>
        <span className="cc-card-header-sub" />
        <span className={`cc-badge ${data?.encontrado ? 'cc-badge-success' : 'cc-badge-muted'}`}>
          {data?.encontrado ? 'Encontrado' : 'Não encontrado'}
        </span>
        <span className="cc-badge cc-badge-muted">DataNexus</span>
      </div>
      <div className="cc-card-body">
        {data?.encontrado && !isVazio(dados) ? (
          <BlocoDados
            titulo={Array.isArray(dados) ? 'Resultados' : null}
            valor={dados}
            onConsultarCpf={onConsultarCpf}
          />
        ) : (
          <div className="cc-alert-info" style={{ marginBottom: 0 }}>
            Nenhum resultado encontrado para esta consulta.
            {data?.erro && <div style={{ marginTop: '0.5rem' }}>{data.erro}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const isRespostaDatanexus = (resposta) =>
  Boolean(resposta) && Object.prototype.hasOwnProperty.call(resposta, 'dados');

function HistoricoCadastral() {
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [usuarios, setUsuarios] = useState([]);
  const [filters, setFilters] = useState({
    tipo: '',
    valor: '',
    usuario_id: '',
    data_inicio: '',
    data_fim: '',
    encontrado: ''
  });
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    usersService.getAll().then((data) => {
      setUsuarios(Array.isArray(data) ? data : data.data || []);
    }).catch(() => setUsuarios([]));
  }, []);

  const fetchHistorico = useCallback(async () => {
    setLoading(true);
    try {
      const clean = {};
      if (filters.tipo) clean.tipo = filters.tipo;
      if (filters.valor.trim()) {
        const v = filters.valor.trim();
        clean.valor = /^[\d.\-()/\s]+$/.test(v) && !v.includes('/') ? soDigitos(v) : v;
      }
      if (filters.usuario_id) clean.usuario_id = filters.usuario_id;
      if (filters.data_inicio) clean.data_inicio = filters.data_inicio;
      if (filters.data_fim) clean.data_fim = filters.data_fim;
      if (filters.encontrado !== '') clean.encontrado = filters.encontrado;

      const response = await consultaCadastralService.getHistorico(clean, page, 20);
      if (response.success) {
        setConsultas(response.data.consultas);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchHistorico();
  }, [fetchHistorico]);

  const abrirDetalhe = async (id) => {
    setModalLoading(true);
    try {
      const response = await consultaCadastralService.getHistoricoById(id);
      if (response.success) {
        setModalData(response.data);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao carregar detalhes');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="cc-card" style={{ marginTop: '2rem' }}>
      <div className="cc-card-header">
        <span className="cc-card-header-title">Histórico de consultas</span>
      </div>
      <div className="cc-card-body">
        <form
          className="cc-historico-filters"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            fetchHistorico();
          }}
        >
          <div className="cc-form-group">
            <label className="cc-form-label">Tipo</label>
            <select
              className="cc-form-input"
              value={filters.tipo}
              onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
            >
              <option value="">Todos</option>
              {GRUPOS_TIPOS.map((g) => (
                <optgroup key={g.titulo} label={g.titulo}>
                  {g.tipos.map((t) => (
                    <option key={t} value={t}>{TIPOS[t].label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="cc-form-group">
            <label className="cc-form-label">Valor consultado</label>
            <input
              className="cc-form-input"
              value={filters.valor}
              onChange={(e) => setFilters({ ...filters, valor: e.target.value })}
              placeholder="CPF, nome, e-mail..."
            />
          </div>
          <div className="cc-form-group">
            <label className="cc-form-label">Consultor</label>
            <select
              className="cc-form-input"
              value={filters.usuario_id}
              onChange={(e) => setFilters({ ...filters, usuario_id: e.target.value })}
            >
              <option value="">Todos</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>{u.nome}</option>
              ))}
            </select>
          </div>
          <div className="cc-form-group">
            <label className="cc-form-label">De</label>
            <input
              type="date"
              className="cc-form-input"
              value={filters.data_inicio}
              onChange={(e) => setFilters({ ...filters, data_inicio: e.target.value })}
            />
          </div>
          <div className="cc-form-group">
            <label className="cc-form-label">Até</label>
            <input
              type="date"
              className="cc-form-input"
              value={filters.data_fim}
              onChange={(e) => setFilters({ ...filters, data_fim: e.target.value })}
            />
          </div>
          <div className="cc-form-group">
            <label className="cc-form-label">Encontrado</label>
            <select
              className="cc-form-input"
              value={filters.encontrado}
              onChange={(e) => setFilters({ ...filters, encontrado: e.target.value })}
            >
              <option value="">Todos</option>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          </div>
          <button type="submit" className="cc-btn cc-btn-primary">Filtrar</button>
        </form>

        {error && <div className="cc-error">{error}</div>}

        {loading ? (
          <p style={{ color: 'var(--texto-secundario)' }}>Carregando...</p>
        ) : (
          <>
            <TabelaSimples
              colunas={[
                {
                  key: 'created_at',
                  label: 'Data',
                  render: (r) => formatDateTime(r.created_at)
                },
                {
                  key: 'usuario',
                  label: 'Consultor',
                  render: (r) => fmt(r.usuario?.nome)
                },
                {
                  key: 'tipo',
                  label: 'Tipo',
                  render: (r) => (
                    <span className="cc-badge cc-badge-tipo">
                      {TIPOS[r.tipo || 'cpf']?.label || r.tipo}
                    </span>
                  )
                },
                {
                  key: 'valor',
                  label: 'Valor',
                  render: (r) => formatValorConsulta(r.tipo || 'cpf', r.valor || r.cpf)
                },
                { key: 'nome', label: 'Nome' },
                {
                  key: 'encontrado',
                  label: 'Status',
                  render: (r) => (
                    <span className={`cc-badge ${r.encontrado ? 'cc-badge-success' : 'cc-badge-muted'}`}>
                      {r.encontrado ? 'Encontrado' : 'Não encontrado'}
                    </span>
                  )
                },
                {
                  key: 'acoes',
                  label: '',
                  render: (r) => (
                    <button type="button" className="cc-btn cc-btn-secondary" onClick={() => abrirDetalhe(r.id)}>
                      Ver detalhes
                    </button>
                  )
                }
              ]}
              linhas={consultas}
              vazio="Nenhuma consulta registrada."
            />
            <div className="cc-pagination">
              <button
                type="button"
                className="cc-btn cc-btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span style={{ color: 'var(--texto-secundario)' }}>
                Página {page} de {totalPages} ({total} registros)
              </span>
              <button
                type="button"
                className="cc-btn cc-btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          </>
        )}
      </div>

      {(modalData || modalLoading) && (
        <div className="cc-modal-overlay" onClick={() => !modalLoading && setModalData(null)}>
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <strong>Detalhes da consulta</strong>
              <button type="button" className="cc-close" onClick={() => setModalData(null)}>×</button>
            </div>
            <div className="cc-modal-body">
              {modalLoading && <p>Carregando...</p>}
              {!modalLoading && modalData?.resposta && (
                isRespostaDatanexus(modalData.resposta)
                  ? <ResultadoDatanexus data={modalData.resposta} />
                  : <ResultadoCadastral data={modalData.resposta} />
              )}
              {!modalLoading && modalData && !modalData.resposta && (
                <div className="cc-alert-info">
                  {modalData.erro || 'Consulta sem dados salvos.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ConsultaCadastral = () => {
  const { user, hasRole, isAdminOrDiretor } = useAuth();
  const [tipo, setTipo] = useState('cpf');
  const [valor, setValor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);
  const [resultadoAnterior, setResultadoAnterior] = useState(null);

  const podeAcessar =
    hasRole('admin', 'diretor') || user?.acesso_consulta_cadastral === true;

  if (!podeAcessar) {
    return <Navigate to="/dashboard" replace />;
  }

  const config = TIPOS[tipo];

  const selecionarTipo = (novoTipo) => {
    if (novoTipo === tipo) return;
    const mesmoFormatoCpf = TIPOS_CPF.includes(tipo) && TIPOS_CPF.includes(novoTipo);
    setTipo(novoTipo);
    if (!mesmoFormatoCpf) setValor('');
    setError('');
  };

  const executarConsulta = async (tipoConsulta, valorBruto) => {
    const v = String(valorBruto || '').trim();
    if (!v) {
      setError(`Informe ${TIPOS[tipoConsulta].campo.toLowerCase()}`);
      return null;
    }
    if (TIPOS_CPF.includes(tipoConsulta) && soDigitos(v).length !== 11) {
      setError('CPF deve conter 11 dígitos');
      return null;
    }

    setLoading(true);
    setError('');
    setResultado(null);
    try {
      const response = tipoConsulta === 'cpf'
        ? await consultaCadastralService.consultar(soDigitos(v))
        : await consultaCadastralService.consultarDatanexus(tipoConsulta, v);
      if (response.success) {
        setResultado(response.data);
        return response.data;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao realizar a consulta');
    } finally {
      setLoading(false);
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setResultadoAnterior(null);
    executarConsulta(tipo, valor);
  };

  const consultarCpfDoResultado = (cpf) => {
    setResultadoAnterior(resultado);
    setTipo('cpf');
    setValor(formatCPFInput(cpf));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    executarConsulta('cpf', cpf);
  };

  const voltarResultadoAnterior = () => {
    setTipo(resultadoAnterior.tipo);
    setValor(TIPOS[resultadoAnterior.tipo]?.mask
      ? TIPOS[resultadoAnterior.tipo].mask(resultadoAnterior.valor || '')
      : resultadoAnterior.valor || '');
    setResultado(resultadoAnterior);
    setResultadoAnterior(null);
  };

  const ehCadastral = resultado && !isRespostaDatanexus(resultado);

  return (
    <Layout>
      <div className="cc-container">
        <div className="cc-header">
          <div>
            <h1 className="cc-title">Consulta Cadastral</h1>
            <p className="cc-subtitle">Consultas financeiras e de pessoas</p>
          </div>
        </div>

        <div className="cc-card">
          <div className="cc-card-header">
            <span className="cc-card-header-title">Nova consulta</span>
          </div>
          <div className="cc-card-body">
            <div className="cc-tipo-grupos">
              {GRUPOS_TIPOS.map((grupo) => (
                <div key={grupo.titulo} className="cc-tipo-grupo">
                  <div className="cc-tipo-grupo-titulo">{grupo.titulo}</div>
                  <div className="cc-tipo-pills">
                    {grupo.tipos.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`cc-tipo-pill ${tipo === t ? 'active' : ''}`}
                        onClick={() => selecionarTipo(t)}
                        disabled={loading}
                      >
                        {TIPOS[t].label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <form className="cc-busca-linha" onSubmit={handleSubmit}>
              <div className="cc-form-group">
                <label className="cc-form-label">{config.campo}</label>
                <input
                  className="cc-form-input"
                  type={config.type || 'text'}
                  value={valor}
                  onChange={(e) => {
                    setValor(config.mask ? config.mask(e.target.value) : e.target.value);
                    setError('');
                  }}
                  placeholder={config.placeholder}
                  maxLength={config.maxLength}
                  autoComplete="off"
                />
              </div>
              <button type="submit" className="cc-btn cc-btn-primary" disabled={loading}>
                {loading ? 'Consultando...' : 'Consultar'}
              </button>
            </form>
            {config.dica && <div className="cc-busca-dica">{config.dica}</div>}
            {error && <div className="cc-error" style={{ marginTop: '1rem', marginBottom: 0 }}>{error}</div>}
          </div>
        </div>

        {resultadoAnterior && !loading && (
          <button type="button" className="cc-btn cc-btn-secondary cc-voltar" onClick={voltarResultadoAnterior}>
            ← Voltar para a consulta por {TIPOS[resultadoAnterior.tipo]?.label || resultadoAnterior.tipo}
          </button>
        )}

        {ehCadastral && !resultado.encontrado && (
          <div className="cc-alert-info">
            Nenhum cadastro encontrado para este CPF.
            {resultado.erro && <div style={{ marginTop: '0.5rem' }}>{resultado.erro}</div>}
          </div>
        )}

        {ehCadastral && resultado.encontrado && <ResultadoCadastral data={resultado} />}

        {resultado && !ehCadastral && (
          <ResultadoDatanexus data={resultado} onConsultarCpf={consultarCpfDoResultado} />
        )}

        {isAdminOrDiretor() && <HistoricoCadastral />}
      </div>
    </Layout>
  );
};

export default ConsultaCadastral;
